// src/components/authorizations/CredentialRotateSection.tsx
//
// "Replace key" for an authorization whose connector uses the generic
// credential form (catalog plan 2026-10-13, step 0.5). Data-driven: the
// connector is found in the catalogue by the authorization's type
// (`auth_type` + `has_credential_form`), its form is fetched, and only the
// `secret` fields are shown. The non-secret values (a store address, a
// subdomain) stay as stored: the backend tests the new secrets against them
// and refuses a key that belongs to another account.
//
// Renders nothing when the type has no credential form, when the form cannot
// be loaded (403 for a non-admin, 404 for a connector without one), or when
// the catalogue is unavailable: a section that cannot work is not shown.
import { useEffect, useState } from "react";
import { Alert, Button, Divider, Group, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  rotateConnectorCredentials,
  type CredentialValues,
} from "@/api/authorizations";
import { listConnectors } from "@/api/managedSources";
import {
  CredentialFieldInput,
  requiredFilled,
  useCredentialForm,
} from "./CredentialConnectionForm";

type Props = {
  authorizationId: string;
  /** The authorization's type, e.g. "WOOCOMMERCE" (a catalogue `auth_type`). */
  authorizationType: string;
  isAccountAdmin: boolean;
  onChanged?: () => void;
};

/** The one catalogue connector with a credential form for this auth type. */
function useCredentialConnectorType(
  authorizationType: string,
  enabled: boolean,
): string | null {
  const [connectorType, setConnectorType] = useState<string | null>(null);
  useEffect(() => {
    setConnectorType(null);
    if (!enabled || !authorizationType) return;
    let cancelled = false;
    listConnectors()
      .then((connectors) => {
        if (cancelled) return;
        const matches = connectors.filter(
          (c) => c.has_credential_form && c.auth_type === authorizationType,
        );
        // Ambiguous (two connectors share an auth type) is the backend's
        // refusal too; show nothing rather than guess.
        setConnectorType(matches.length === 1 ? matches[0].connector_type : null);
      })
      .catch(() => {
        if (!cancelled) setConnectorType(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authorizationType, enabled]);
  return connectorType;
}

export function CredentialRotateSection({
  authorizationId,
  authorizationType,
  isAccountAdmin,
  onChanged,
}: Props) {
  // The form route is admin-only; a non-admin cannot rotate anyway.
  const connectorType = useCredentialConnectorType(
    authorizationType,
    isAccountAdmin,
  );
  const { form } = useCredentialForm(connectorType);
  const [values, setValues] = useState<CredentialValues>({});
  const [errorText, setErrorText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A different authorization starts with an empty form.
  useEffect(() => {
    setValues({});
    setErrorText(null);
  }, [authorizationId]);

  if (!connectorType || !form) return null;
  const secretFields = form.fields.filter((f) => f.kind === "secret");
  if (secretFields.length === 0) return null;

  const canSave = requiredFilled(secretFields, values);
  const secretNames = secretFields.map((f) => f.label.toLowerCase());
  const secretPhrase =
    secretNames.length === 1
      ? secretNames[0]
      : `${secretNames.slice(0, -1).join(", ")} and ${secretNames[secretNames.length - 1]}`;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setErrorText(null);
    try {
      const out: CredentialValues = {};
      for (const f of secretFields) {
        const v = (values[f.name] ?? "").trim();
        if (v) out[f.name] = v;
      }
      await rotateConnectorCredentials(authorizationId, out);
      notifications.show({
        title: "Key replaced",
        message: "The connection has been reconnected.",
        color: "green",
      });
      setValues({});
      onChanged?.();
    } catch (err: unknown) {
      setErrorText(
        err instanceof Error && err.message ? err.message : "Something went wrong",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Divider />
      <Stack gap="xs">
        <Text fw={600}>Replace key</Text>
        <Text size="sm" c="dimmed">
          Paste a new {secretPhrase} from {form.label}. It is tested against the
          same {form.label} account before it is saved.
        </Text>

        {secretFields.map((field) => (
          <CredentialFieldInput
            key={field.name}
            field={field}
            labelPrefix="New"
            value={values[field.name] ?? ""}
            onChange={(v) => {
              setValues((prev) => ({ ...prev, [field.name]: v }));
              setErrorText(null);
            }}
          />
        ))}

        {errorText && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="Could not replace the key"
          >
            {errorText}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Replace key
          </Button>
        </Group>
      </Stack>
    </>
  );
}
